import React from 'react';
import type { TravelerInformationResponseArgs } from '@this/domain/traveler_information';
import { GENDER_LABELS } from '@this/domain/traveler_information';

export const TRAVELER_DISPLAY_TYPE = {
  HOTEL: 'hotel',
  FLIGHT: 'flight',
  SHINKANSEN: 'shinkansen'
} as const;

export type TravelerDisplayType = typeof TRAVELER_DISPLAY_TYPE[keyof typeof TRAVELER_DISPLAY_TYPE];

interface Props {
  travelerInfo: TravelerInformationResponseArgs;
  displayType: TravelerDisplayType;
}

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
};

const TravelerInfoDisplay = ({ travelerInfo, displayType }: Props) => {
  const nameKana = [travelerInfo.last_name_kana, travelerInfo.first_name_kana].filter(Boolean).join(' ');
  const nameRoman = [travelerInfo.last_name_roman, travelerInfo.first_name_roman].filter(Boolean).join(' ');
  const flightNameKana = [travelerInfo.flight_last_name_kana, travelerInfo.flight_first_name_kana]
    .filter(Boolean)
    .join(' ');
  const flightNameRoman = [travelerInfo.flight_last_name_roman, travelerInfo.flight_first_name_roman]
    .filter(Boolean)
    .join(' ');

  const isHotel = displayType === TRAVELER_DISPLAY_TYPE.HOTEL;
  const isFlight = displayType === TRAVELER_DISPLAY_TYPE.FLIGHT;
  const isShinkansen = displayType === TRAVELER_DISPLAY_TYPE.SHINKANSEN;

  const displayNameKana = isFlight || isHotel ? flightNameKana || nameKana : nameKana;
  const displayNameRoman = isFlight || isHotel ? flightNameRoman || nameRoman : nameRoman;
  const displayGender = isFlight && travelerInfo.flight_gender ? GENDER_LABELS[travelerInfo.flight_gender] : '';
  const displayBirthday = isFlight
    ? formatDate(travelerInfo.flight_birthday || travelerInfo.birthday)
    : formatDate(travelerInfo.birthday);
  const displayTel = travelerInfo.flight_tel || '';

  const hasBaseInfo = displayNameKana || displayNameRoman || displayGender || displayBirthday || displayTel;

  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0, lineHeight: 1.8 }}>
      {displayNameKana && <li>氏名(カナ): {displayNameKana}</li>}
      {displayNameRoman && <li>氏名(ローマ字): {displayNameRoman}</li>}
      {displayGender && <li>性別: {displayGender}</li>}
      {displayBirthday && <li>生年月日: {displayBirthday}</li>}
      {isHotel && displayTel && <li>連絡先電話番号: {displayTel}</li>}
      {isFlight && displayTel && <li>緊急連絡先: {displayTel}</li>}
      {isFlight && travelerInfo.air_seat && <li>座席の希望: {travelerInfo.air_seat}</li>}
      {isShinkansen && travelerInfo.shinkansen_seat_type_text && (
        <li>座席タイプ: {travelerInfo.shinkansen_seat_type_text}</li>
      )}
      {isShinkansen && travelerInfo.shinkansen_seat && <li>座席の希望: {travelerInfo.shinkansen_seat}</li>}
      {!hasBaseInfo &&
        !travelerInfo.air_seat &&
        !travelerInfo.shinkansen_seat_type_text &&
        !travelerInfo.shinkansen_seat && <li>情報がありません</li>}
    </ul>
  );
};

export default TravelerInfoDisplay;
